import { useEffect, useRef, type ReactNode, type RefObject } from 'react';
import { cn } from './cn';

/**
 * Shared modal sheet primitive (ADR-014 Amendment 7 / ADR-015 amendment 2026-07-09).
 * Extracts the proven native `<dialog>` mechanics first shipped in `ModuleLauncher`
 * (PRO-0350): `showModal()` for a real top-layer modal (browser-native focus trap +
 * inert background), Esc-to-close via `onCancel`, and focus return to the trigger on
 * close (NF-10). Anchored left/right for drawers or centred for dialogs, on the
 * sanctioned `.surface-glass` treatment (never ad-hoc glass CSS).
 *
 * Controlled: the caller owns `open` and the trigger; the sheet drives the dialog
 * element from the `open` prop and calls `onClose` for every dismissal path (Esc,
 * backdrop, programmatic).
 *
 * Open motion: after `showModal()` we paint the `data-state="closed"` frame, then flip
 * to `data-state="open"` on the next frames so the CSS transition (index.css
 * `.sheet-panel`) interpolates transform/opacity. `data-state` is set imperatively (not
 * a React-controlled prop) so re-renders cannot strip it mid-flight. Reduced motion is
 * honoured by the global `transition-duration: 0.01ms` rule in index.css (CF-001) — if
 * the OS has "reduce motion" on, the sheet appears instantly (by design).
 */
export type SheetAnchor = 'left' | 'right' | 'center';
export type SheetTone = 'glass' | 'glass-sidebar';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  /** Focus returns here on close — usually the trigger button. */
  returnFocusRef?: RefObject<HTMLElement | null>;
  anchor?: SheetAnchor;
  tone?: SheetTone;
  id?: string;
  'aria-labelledby'?: string;
  'aria-label'?: string;
  /** Sizing overrides (width / max-width). */
  className?: string;
  children: ReactNode;
}

const anchorClasses: Record<SheetAnchor, string> = {
  // Clear the opposing edge: native <dialog> UA styles set left:0 (+ margin:auto),
  // which would otherwise pin a right-anchored sheet to the left despite right-0.
  left: 'fixed inset-y-0 left-0 right-auto m-0 h-full max-h-none w-[85%] max-w-xs rounded-none rounded-r-lg',
  right:
    'fixed inset-y-0 right-0 left-auto m-0 h-full max-h-none w-[85%] max-w-xs rounded-none rounded-l-lg',
  center: 'm-auto max-h-[85vh] w-[calc(100%-2rem)] max-w-md rounded-lg',
};

export function Sheet({
  open,
  onClose,
  returnFocusRef,
  anchor = 'center',
  tone = 'glass',
  id,
  'aria-labelledby': ariaLabelledBy,
  'aria-label': ariaLabel,
  className,
  children,
}: SheetProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) {
      return undefined;
    }

    if (open) {
      if (!dialog.open) {
        if (typeof dialog.showModal === 'function') {
          dialog.showModal();
        } else {
          dialog.setAttribute('open', '');
        }
      }
      // Paint the off-screen "closed" frame, then flip to "open" after two frames so
      // the browser has committed the start value and the transition actually runs.
      dialog.setAttribute('data-state', 'closed');
      let raf2 = 0;
      const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => {
          dialog.setAttribute('data-state', 'open');
        });
      });
      const focusable = dialog.querySelector<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      focusable?.focus();
      return () => {
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
      };
    }

    if (dialog.open) {
      if (typeof dialog.close === 'function') {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
      }
      dialog.removeAttribute('data-state');
    }
    return undefined;
  }, [open]);

  // Single dismissal path: close + return focus to the trigger (NF-10). Used by Esc
  // (onCancel), backdrop click, and the native close event so focus never strands.
  const requestClose = () => {
    onClose();
    returnFocusRef?.current?.focus();
  };

  return (
    // Native <dialog>: keyboard dismissal is handled by onCancel (Esc). The onClick
    // only implements backdrop-click-to-dismiss (mouse convenience), so no keyboard
    // listener is needed on the element itself (NF-10 keyboard path is Esc).
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <dialog
      ref={dialogRef}
      id={id}
      data-anchor={anchor}
      aria-labelledby={ariaLabelledBy}
      aria-label={ariaLabel}
      className={cn(
        'sheet-panel border border-border p-0 text-text-primary shadow-md outline-none backdrop:bg-black/40',
        tone === 'glass-sidebar' ? 'surface-glass-sidebar' : 'surface-glass',
        anchorClasses[anchor],
        className,
      )}
      onCancel={(event) => {
        event.preventDefault();
        requestClose();
      }}
      onClick={(event) => {
        if (event.target === dialogRef.current) {
          requestClose();
        }
      }}
    >
      {children}
    </dialog>
  );
}
